import { Alert, Button, Select, Slider, Space, theme } from 'antd';
import React from 'react';
import {
  filesFor,
  formatTime,
  locateFile,
  type Recording,
} from '@/prototype/domains/recording';
import styles from './index.module.less';

export interface AudioWorkspaceProps {
  recording: Recording;
  range?: { startMs: number; endMs: number };
  onTime?: (ms: number) => void;
  compact?: boolean;
}
export default function AudioWorkspace({
  recording,
  range,
  onTime,
  compact = false,
}: AudioWorkspaceProps) {
  const { token } = theme.useToken();
  const player = React.useRef<HTMLAudioElement>(null);
  const files = React.useMemo(
    () => filesFor(recording),
    [
      recording.uuid,
      recording.fixture,
      recording.hash,
      recording.audioBlob?.size,
      recording.audioBlob?.type,
      recording.fields?.recordingFiles,
    ],
  );
  const [sources, setSources] = React.useState<string[]>([]);
  const [index, setIndex] = React.useState(0);
  const [time, setTime] = React.useState(0);
  const [rate, setRate] = React.useState(1);
  const [loop, setLoop] = React.useState(false);
  const [error, setError] = React.useState('');
  const [peaks, setPeaks] = React.useState<number[]>([]);
  const pending = React.useRef<{ local: number; play: boolean } | undefined>(
    undefined,
  );
  const total = Math.max(0, ...files.map((f) => f.offsetMs + f.durationMs));
  const seek = React.useCallback(
    (ms: number, play = false) => {
      const target = Math.max(
        compact && range ? range.startMs : 0,
        Math.min(ms, compact && range ? range.endMs : total),
      );
      const next = locateFile(files, target);
      if (next < 0) {
        setError('该时刻没有可用音频文件，请从文件列表检查来源');
        return;
      }
      const local = (target - files[next].offsetMs) / 1000;
      setTime(target);
      if (next !== index || !player.current?.readyState) {
        pending.current = { local, play };
        setIndex(next);
      } else if (player.current) {
        player.current.currentTime = local;
        if (play)
          void player.current
            .play()
            .catch(() => setError('播放受阻，请点击播放器播放按钮重试'));
      }
    },
    [files, index, total, compact, range?.startMs, range?.endMs],
  );
  const seekRef = React.useRef(seek);
  seekRef.current = seek;
  React.useEffect(() => {
    const urls = files.map((f) =>
      f.blob ? URL.createObjectURL(f.blob) : f.url || '',
    );
    setSources(urls);
    setIndex(0);
    setError('');
    return () => {
      player.current?.pause();
      urls.forEach((url, i) => {
        if (files[i].blob) URL.revokeObjectURL(url);
      });
    };
  }, [files]);
  React.useEffect(() => {
    if (range) seekRef.current(range.startMs);
  }, [range?.startMs, range?.endMs, recording.uuid]);
  React.useEffect(() => {
    const url = sources[index];
    if (!url) return;
    const abort = new AbortController();
    let active = true;
    let context: AudioContext | undefined;
    setPeaks([]);
    void (async () => {
      try {
        context = new AudioContext();
        const response = await fetch(url, { signal: abort.signal });
        if (!response.ok) throw new Error('音频不可用');
        const buffer = await context.decodeAudioData(
          await response.arrayBuffer(),
        );
        const data = buffer.getChannelData(0);
        const step = Math.max(1, Math.floor(data.length / 120));
        const values = Array.from({ length: 120 }, (_, i) => {
          let peak = 0;
          for (
            let j = i * step;
            j < Math.min(data.length, (i + 1) * step);
            j += 8
          )
            peak = Math.max(peak, Math.abs(data[j]));
          return peak;
        });
        if (active) setPeaks(values);
      } catch {
        /* Native controls remain usable without decoded waveform. */
      } finally {
        if (context && context.state !== 'closed') void context.close();
      }
    })();
    return () => {
      active = false;
      abort.abort();
    };
  }, [sources, index]);
  React.useEffect(() => {
    if (compact) return;
    const handler = (event: KeyboardEvent) => {
      if (
        event.code !== 'Space' ||
        (event.target instanceof HTMLElement &&
          event.target.closest(
            'input,textarea,select,button,audio,[contenteditable=true],[role=combobox],[role=slider]',
          ))
      )
        return;
      event.preventDefault();
      if (player.current?.paused)
        void player.current.play().catch(() => setError('请点击播放重试'));
      else player.current?.pause();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [compact]);
  if (!files.length)
    return (
      <Alert
        type="warning"
        showIcon
        title="音频文件缺失"
        description="可阅读已授权转写；请从手动导入重新选择音频来源。"
      />
    );
  return (
    <section className={styles.player} aria-label="音频工作区">
      {error && (
        <Alert
          type="warning"
          title={error}
          action={
            <Button
              onClick={() => {
                setError('');
                player.current?.load();
              }}
            >
              重新加载
            </Button>
          }
        />
      )}
      <div
        className={styles.wave}
        role="img"
        aria-label={
          peaks.length ? '当前文件实际音频波形' : '波形不可用，可使用下方进度条'
        }
      >
        {peaks.length ? (
          <svg
            viewBox="0 0 120 64"
            preserveAspectRatio="none"
            width="100%"
            height="64"
            aria-hidden="true"
          >
            <path
              fill="none"
              stroke="currentColor"
              strokeWidth="0.7"
              d={peaks
                .map((peak, i) => `M${i},${32 - peak * 30}V${32 + peak * 30}`)
                .join(' ')}
            />
          </svg>
        ) : (
          '使用进度条定位音频'
        )}
      </div>
      <audio
        ref={player}
        src={sources[index]}
        controls
        preload="metadata"
        aria-label={compact ? '证据片段播放器' : '录音播放器'}
        onError={() =>
          setError('音频读取或解码失败，请重试或从手动导入补充文件')
        }
        onLoadedMetadata={() => {
          const audio = player.current;
          if (!audio) return;
          audio.playbackRate = rate;
          const target = pending.current;
          if (target) {
            audio.currentTime = Math.min(target.local, audio.duration);
            pending.current = undefined;
            if (target.play)
              void audio.play().catch(() => setError('请点击播放重试'));
          }
        }}
        onPlay={() => {
          const audio = player.current;
          if (compact && range && audio) {
            const at = files[index].offsetMs + audio.currentTime * 1000;
            if (at < range.startMs || at >= range.endMs)
              seek(range.startMs, true);
          }
        }}
        onTimeUpdate={() => {
          const ms =
            files[index].offsetMs + (player.current?.currentTime || 0) * 1000;
          if (loop && range && ms >= range.endMs) seek(range.startMs, true);
          else if (compact && range && ms >= range.endMs) {
            player.current?.pause();
            setTime(range.endMs);
          } else {
            setTime(ms);
            onTime?.(ms);
          }
        }}
        onEnded={() => {
          if (loop && range) seek(range.startMs, true);
          else if (
            index < files.length - 1 &&
            (!compact || !range || files[index + 1].offsetMs < range.endMs)
          )
            seek(files[index + 1].offsetMs, true);
        }}
      >
        <track
          kind="captions"
          srcLang="zh-CN"
          label="中文转写"
          src={`data:text/vtt;charset=utf-8,${encodeURIComponent('WEBVTT\n\n' + (recording.versions?.[0]?.segments || []).map((s) => `${new Date(s.startMs).toISOString().slice(11, 23)} --> ${new Date(s.endMs).toISOString().slice(11, 23)}\n${s.speaker}：${s.text}\n`).join('\n'))}`}
        />
      </audio>
      <Slider
        aria-label="业务录音全局进度"
        min={compact && range ? range.startMs : 0}
        max={compact && range ? range.endMs : total}
        value={time}
        tooltip={{ formatter: (value) => formatTime(value || 0) }}
        onChange={(value) => seek(value)}
      />
      <Space wrap>
        <strong>
          {formatTime(time)} / {formatTime(total)}
        </strong>
        {!compact && <Button onClick={() => seek(time - 5000)}>前退5秒</Button>}
        {!compact && <Button onClick={() => seek(time + 5000)}>前进5秒</Button>}
        <Select
          aria-label="播放倍速"
          value={rate}
          options={[0.75, 1, 1.25, 1.5, 2].map((value) => ({
            value,
            label: `${value}×`,
          }))}
          onChange={(value) => {
            setRate(value);
            if (player.current) player.current.playbackRate = value;
          }}
        />
        <Button
          disabled={!range}
          type={loop ? 'primary' : 'default'}
          onClick={() => {
            setLoop(!loop);
            if (range) seek(range.startMs);
          }}
        >
          片段循环{loop ? ' · 开' : ''}
        </Button>
        <span>
          文件 {index + 1}/{files.length}
        </span>
      </Space>
    </section>
  );
}
